import { type PermissionCodeTypes } from './permission';

export interface BusinessProperty {
    key: string;
    value: string;
    restricted: boolean;
}

export type PairRegisterResponse = {
    businessId: string;
    businessName: string;
    businessLogo: string;
    branchId: string;
    branchName: string;
    token: string;
    registerName: string;
    registerIndex: number;
    deviceType: RegisterDeviceType;
};

export const REGISTER_DEVICE_TYPE = {
    MONIEPOINT_TERMINAL: 'MONIEPOINT_TERMINAL',
    OTHERS: 'OTHERS',
} as const;

export type RegisterDeviceType =
    (typeof REGISTER_DEVICE_TYPE)[keyof typeof REGISTER_DEVICE_TYPE];

export interface BusinessInfo {
    businessId: string;
    businessName: string;
    businessLogo: string;
    branchId: string;
    branchName: string;
    registerName: string;
    registerIndex: number;
}

export interface MappedConfigurationResponse {
    businessName: string;
    businessLogo: string;
    branchName: string;
    businessPhoneNumber: string;
    businessId: string;
    branchId: string;
    registerIndex: number;
    tid: string;
    registerId: string;
    registerPrintUtilityURL: string;
    registerName: string;
    authenticatePerSale: boolean;
    receiptAutoPrinting: boolean;
    taxConfiguration: MappedTaxResponse[];
    paymentMethodConfiguration: MappedPaymentMethodResponse[];
    receiptConfiguration: ReceiptConfiguration;
    invoiceConfiguration: InvoiceConfiguration;
    creditLimit?: number;
    orderNoteCompulsoryOnSale: boolean;
    customerCompulsoryOnSale: boolean;
    limitSaleToOneItem: boolean;
    restrictSaleOnOutOfStock: boolean;
    address: string;
    allowCreditSales: boolean;
    allowChangeAddToWallet: boolean;
    enableQuickCart: boolean;
    showStockCount: boolean;
}

export interface MappedStaffResponse {
    id: string;
    authUserId: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    registerPin: string;
    permissionList: PermissionCodeTypes[];
    deleted: boolean;
    superRole: boolean;
    active: boolean;
}

export const PAYMENT_METHOD_TYPE = {
    CASH: 'CASH',
    MONIEPOINT_CARD: 'MONIEPOINT_CARD',
    MONIEPOINT_TRANSFER: 'MONIEPOINT_TRANSFER',
    OTHER: 'OTHER',
    MONIEPOINT_TERMINAL: 'MONIEPOINT_TERMINAL',
    CUSTOMER_WALLET: 'CUSTOMER_WALLET',
    LOYALTY_POINTS: 'LOYALTY_POINTS',
} as const;

export type PaymentMethodType =
    (typeof PAYMENT_METHOD_TYPE)[keyof typeof PAYMENT_METHOD_TYPE];

export interface MappedPaymentMethodResponse {
    id: string;
    name: string;
    type: PaymentMethodType;
    paymentMethodIndex: number;
    createdAt: string;
    updatedAt: string;
    deleted: boolean;
    enabled: boolean;
}

export interface MappedTaxResponse {
    id: string;
    name: string;
    value: number;
    taxType: TaxType;
    includedInTotal: boolean;
    createdAt: string;
    updatedAt: string;
    deleted: boolean;
}

export const TAX_TYPE = {
    ITEM_SPECIFIC: 'ITEM_SPECIFIC',
    GLOBAL: 'GLOBAL',
} as const;

export type TaxType = (typeof TAX_TYPE)[keyof typeof TAX_TYPE];

export const RECEIPT_FONT_TIER = {
    SMALL: 'SMALL',
    MEDIUM: 'MEDIUM',
    LARGE: 'LARGE',
    EXTRA_LARGE: 'EXTRA_LARGE',
} as const;

export type ReceiptFontTier =
    (typeof RECEIPT_FONT_TIER)[keyof typeof RECEIPT_FONT_TIER];

export interface ReceiptConfiguration {
    id: string;
    header: string;
    footer: string;
    receiptFontTier: ReceiptFontTier;
    fieldVisibilityConfig: {
        business_name: boolean;
        business_logo: boolean;
        business_address: boolean;
        business_phone_number: boolean;
        cashier_name: boolean;
        register_name: boolean;
        date_time: boolean;
        customer_name: boolean;
        customer_email: boolean;
        customer_phone_number: boolean;
        header_text: boolean;
        footer_text: boolean;
    };
    createdAt: string;
    updatedAt: string;
    version: string;
    deleted: boolean;
}

export interface InvoiceConfiguration {
    id: string;
    invoiceFieldVisibilityConfig: {
        business_logo: boolean;
        terms_and_conditions: boolean;
        business_address: boolean;
        due_date: boolean;
        signature_lines: boolean;
        status: boolean;
    };
    termsAndConditionsText: string;
}

export const EMPTY_RECEIPT_CONFIG = {} as Partial<ReceiptConfiguration>;
